import { useSelector } from 'react-redux';

import { LOCAL_STORAGE_KEYS } from '@constants/storage';
import { RoleScope } from '@features/team-management/types/customRoles';
import {
  hasAnyRoleScope,
  isSalesAgent as isSalesAgentScope
} from '@helpers/roleScope';
import { getLocalStorageItem } from '@utils/storage';

import { useGetLeadOrgConfigQuery } from '../queries/LeadManagementQuery';

const BULK_ASSIGN_SCOPES: readonly RoleScope[] = [
  RoleScope.OWNER,
  RoleScope.ORGANISATION_ADMIN,
  RoleScope.SALES_MANAGER
];

/**
 * Org LMS rule: when `is_lead_assignment_allowed_for_agent` is false, sales agents
 * must not change assignee or use bulk assignee actions. Lead creation is limited
 * to manual add only (CSV import and Meta sync are hidden).
 * Org LMS rule: when `is_lead_deletion_allowed_for_sales_agent` is false, sales agents
 * must not delete leads.
 * Config is fetched only for the sales-agent role.
 */
const useLeadAgentRestrictions = () => {
  const organizationId = getLocalStorageItem(
    LOCAL_STORAGE_KEYS.ORGANIZATION_ID
  );
  const { rolesAndPermissions } = useSelector((state: any) => state.user);
  const isSalesAgent = isSalesAgentScope(rolesAndPermissions);

  const { data: orgConfig, isLoading } = useGetLeadOrgConfigQuery(
    { organizationId: organizationId || '' },
    { skip: !organizationId || !isSalesAgent }
  );

  const isAssigneeSelectionDisabled =
    isSalesAgent && orgConfig?.is_lead_assignment_allowed_for_agent === false;

  const isLeadDeletionDisabled =
    isSalesAgent &&
    orgConfig?.is_lead_deletion_allowed_for_sales_agent === false;

  const isAgentReassignAllowed =
    isSalesAgent && orgConfig?.is_lead_assignment_allowed_for_agent === true;

  const isBulkAssignEnabled =
    hasAnyRoleScope(rolesAndPermissions, BULK_ASSIGN_SCOPES) ||
    isAgentReassignAllowed;

  return {
    orgConfig,
    isLoading,
    isSalesAgent,
    isAssigneeSelectionDisabled,
    isLeadDeletionDisabled,
    isBulkAssignEnabled
  };
};

export default useLeadAgentRestrictions;
